<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Button from 'primevue/button';
import ColorPicker from 'primevue/colorpicker';

const props = defineProps<{
  modelValue?: string | null;
  height?: number;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

const canvasRef = ref<HTMLCanvasElement | null>(null);
const drawing = ref(false);
const color = ref('fb7185');
const brushSize = ref(4);

let ctx: CanvasRenderingContext2D | null = null;

function setupCanvas() {
  const canvas = canvasRef.value;
  if (!canvas) return;

  const size = canvas.parentElement?.clientWidth ?? 320;
  canvas.width = size;
  canvas.height = props.height ?? 220;

  ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = `#${color.value}`;
  ctx.lineWidth = brushSize.value;

  if (props.modelValue) {
    const image = new Image();
    image.onload = () => ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);
    image.src = props.modelValue;
  } else {
    ctx.fillStyle = '#fff7fb';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
}

function pointerPosition(event: MouseEvent | TouchEvent) {
  const canvas = canvasRef.value!;
  const rect = canvas.getBoundingClientRect();
  const point = 'touches' in event ? event.touches[0] : event;
  return {
    x: ((point.clientX - rect.left) / rect.width) * canvas.width,
    y: ((point.clientY - rect.top) / rect.height) * canvas.height,
  };
}

function startDraw(event: MouseEvent | TouchEvent) {
  if (!ctx) return;
  drawing.value = true;
  const { x, y } = pointerPosition(event);
  ctx.beginPath();
  ctx.moveTo(x, y);
  event.preventDefault();
}

function draw(event: MouseEvent | TouchEvent) {
  if (!drawing.value || !ctx) return;
  const { x, y } = pointerPosition(event);
  ctx.strokeStyle = `#${color.value}`;
  ctx.lineWidth = brushSize.value;
  ctx.lineTo(x, y);
  ctx.stroke();
  event.preventDefault();
}

function stopDraw() {
  if (!drawing.value) return;
  drawing.value = false;
  exportDrawing();
}

function exportDrawing() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  emit('update:modelValue', canvas.toDataURL('image/jpeg', 0.82));
}

function clearCanvas() {
  const canvas = canvasRef.value;
  if (!canvas || !ctx) return;
  ctx.fillStyle = '#fff7fb';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  emit('update:modelValue', null);
}

watch(color, () => {
  if (ctx) ctx.strokeStyle = `#${color.value}`;
});

watch(brushSize, () => {
  if (ctx) ctx.lineWidth = brushSize.value;
});

onMounted(setupCanvas);
onBeforeUnmount(() => {
  drawing.value = false;
});
</script>

<template>
  <div class="drawing">
    <div class="drawing__toolbar">
      <label class="drawing__tool">
        Цвет
        <ColorPicker v-model="color" format="hex" />
      </label>
      <label class="drawing__tool">
        Кисть
        <input v-model.number="brushSize" type="range" min="2" max="16" />
      </label>
      <Button label="Очистить" icon="pi pi-eraser" severity="secondary" text @click="clearCanvas" />
    </div>
    <canvas
      ref="canvasRef"
      class="drawing__canvas"
      @mousedown="startDraw"
      @mousemove="draw"
      @mouseup="stopDraw"
      @mouseleave="stopDraw"
      @touchstart="startDraw"
      @touchmove="draw"
      @touchend="stopDraw"
    />
  </div>
</template>

<style scoped>
.drawing {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.drawing__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
}

.drawing__tool {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #831843;
}

.drawing__canvas {
  width: 100%;
  border: 2px dashed #fbcfe8;
  border-radius: 1rem;
  background: #fff7fb;
  touch-action: none;
  cursor: crosshair;
}
</style>
